(() => {
  const storageKey = 'ap-bloem-pending-add';
  let pending = null;
  let queue = Promise.resolve();
  const root = () => window.Shopify?.routes?.root || '/';

  async function request(path, body) {
    const response = await fetch(root() + path, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const result = await response.json();
    if (!response.ok || result.errors) {
      throw new Error(result.description || result.message || 'Unable to update the cart. Please try again.');
    }
    return result;
  }

  function save(operation) {
    pending = operation;
    try {
      if (operation) sessionStorage.setItem(storageKey, JSON.stringify(operation));
      else sessionStorage.removeItem(storageKey);
    } catch (_) {}
  }

  function load() {
    if (pending) return pending;
    try {
      return JSON.parse(sessionStorage.getItem(storageKey));
    } catch (_) {
      return null;
    }
  }

  function serialize(action) {
    const result = queue.then(action);
    queue = result.catch(() => {});
    return result;
  }

  function groupItems(cart, id) {
    return cart.items.filter((item) => item.properties?._ap_bloem_group === id);
  }

  async function add(items) {
    const signature = JSON.stringify(items);
    let operation = load();
    if (operation && operation.signature !== signature) {
      const cart = await request('cart.js');
      if (groupItems(cart, operation.id).length) {
        throw new Error('A previous selection is already in your cart. Please review your cart before adding a different selection.');
      }
      save(null);
      operation = null;
    }
    if (!operation) {
      const id = crypto.randomUUID();
      operation = {
        id,
        signature,
        items: items.map((item, index) => ({
          ...item,
          quantity: Number(item.quantity),
          properties: {
            ...item.properties,
            _ap_bloem_group: id,
            _ap_bloem_item: String(index),
            _ap_bloem_role: index === 0 ? 'bouquet' : 'extra',
          },
        })),
      };
      save(operation);
    }

    async function reconcile(cart) {
      const existing = groupItems(cart, operation.id);
      const missing = [];
      for (const wanted of operation.items) {
        const lines = existing.filter((item) => item.properties._ap_bloem_item === wanted.properties._ap_bloem_item);
        if (!lines.length) missing.push(wanted);
        else {
          for (let index = 0; index < lines.length; index++) {
            const quantity = index === 0 ? wanted.quantity : 0;
            if (lines[index].quantity !== quantity) await request('cart/change.js', { id: lines[index].key, quantity });
          }
        }
      }
      return missing;
    }

    const missing = await reconcile(await request('cart.js'));
    if (missing.length) {
      try {
        await request('cart/add.js', { items: missing });
      } catch (error) {
        const remaining = await reconcile(await request('cart.js'));
        if (remaining.length) throw error;
      }
    }
    const remaining = await reconcile(await request('cart.js'));
    if (remaining.length) throw new Error('Some items could not be added. Please retry to finish this selection or review your cart.');
    save(null);
  }

  async function change(key, quantity, sections, sectionsUrl) {
    const cart = await request('cart.js');
    const item = cart.items.find((entry) => entry.key === key);
    if (!item) throw new Error('Your cart has changed. Please refresh the page and try again.');
    const group = item.properties?._ap_bloem_group;
    if (quantity === 0 && group && item.properties._ap_bloem_role === 'bouquet') {
      const updates = Object.fromEntries(groupItems(cart, group).map((entry) => [entry.key, 0]));
      const result = await request('cart/update.js', { updates, sections, sections_url: sectionsUrl });
      if (load()?.id === group) save(null);
      return result;
    }
    return request('cart/change.js', { id: key, quantity, sections, sections_url: sectionsUrl });
  }

  window.apBloemCart = {
    add: (items) => serialize(() => add(items)),
    change: (...args) => serialize(() => change(...args)),
  };
})();
